import React, { useEffect, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { FieldErrors, SubmitHandler, useForm } from "react-hook-form";
import { z } from "zod";

import { HipaaLogo } from "~/icons";
import { planCoverageSchema } from "~/schemas/SignUp/InsuranceProvider";
import { RadioCardGroup } from "~/shared/components/ui/RadioCardGroup";
import { useAutoAdvanceOnSelect } from "~/shared/hooks/useAutoAdvanceOnSelect";
import { useSubStepTracking } from "~/shared/hooks/useFunnelTracking";
import { InsuranceProviderFormProps } from "~/shared/utils/types";
import { useSignUpStore } from "~/stores";
import { getPlanCoverageOptions } from "~/utils";
import { shouldFirePixelEvents } from "../../../../utils/pixelUtils";
import { getUtmParams } from "../../../../utils/utmCapture";
import { ActionButtons } from "../../ActionButtons";
import { ErrorMessage } from "../../ErrorMessage";
import { FormContentContainer } from "../../FormContentContainer";

export const PlanCoverageForm = ({
  onPrev,
  onNext,
  stepId = "primary_insurance",
  isSecondaryProvider = false,
}: InsuranceProviderFormProps & { stepId?: string }) => {
  const { patient, setPatientInsuranceField } = useSignUpStore();
  const { primaryInsurance, secondaryInsurance } = patient;

  const defaultValue = isSecondaryProvider
    ? secondaryInsurance?.planCoverage
    : primaryInsurance.planCoverage;

  const [selectedValue, setSelectedValue] = useState<string>(
    defaultValue ?? "",
  );
  const autoAdvanceOnSelect = useAutoAdvanceOnSelect(Boolean(defaultValue));

  const { trackComplete, trackBack, trackErrors } = useSubStepTracking({
    parentStep: isSecondaryProvider
      ? "secondary_insurance"
      : "primary_insurance",
    subStep: "plan_coverage",
  });

  const {
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<z.infer<typeof planCoverageSchema>>({
    mode: "onBlur",
    resolver: zodResolver(planCoverageSchema),
    defaultValues: {
      planCoverage: defaultValue ?? "",
    },
  });

  useEffect(() => {
    setSelectedValue(defaultValue ?? "");
    setValue("planCoverage", defaultValue ?? "");
  }, [defaultValue, setValue]);

  const providerName = isSecondaryProvider
    ? secondaryInsurance?.providerName
    : primaryInsurance.providerName;

  const planTypeProvider = isSecondaryProvider
    ? secondaryInsurance?.planTypeProvider
    : primaryInsurance.planTypeProvider;

  const planCoverageOptions = getPlanCoverageOptions(
    providerName,
    planTypeProvider,
  );

  // Convert string array to RadioCardGroup format
  const radioOptions = planCoverageOptions.map((option) => ({
    value: option,
    label: option,
  }));

  const handleSelectionChange = (value: string) => {
    setSelectedValue(value);
    setValue("planCoverage", value, { shouldValidate: true });
    // On the frontier (first encounter), a selection advances immediately;
    // on a revisited step the user changes freely and clicks Next.
    if (autoAdvanceOnSelect) {
      void handleSubmit(onSubmit, onError)();
    }
  };

  const onSubmit: SubmitHandler<z.infer<typeof planCoverageSchema>> = (
    data,
  ) => {
    setPatientInsuranceField(
      "planCoverage",
      data.planCoverage,
      isSecondaryProvider,
    );

    trackComplete({ plan_coverage: data.planCoverage });

    if (
      !isSecondaryProvider &&
      data.planCoverage === "PPO" &&
      planTypeProvider === "employer" &&
      shouldFirePixelEvents()
    ) {
      const utm = getUtmParams();
      const fbq = (window as any).fbq;
      if (fbq) {
        fbq("track", "Lead", {
          content_name: "Plan Selected",
          ...(utm?.utm_source && { utm_source: utm.utm_source }),
          ...(utm?.utm_medium && { utm_medium: utm.utm_medium }),
          ...(utm?.utm_campaign && { utm_campaign: utm.utm_campaign }),
          ...(utm?.utm_term && { utm_term: utm.utm_term }),
          ...(utm?.utm_content && { utm_content: utm.utm_content }),
          ...(utm?.ad_set_id && { adset_id: utm.ad_set_id }),
          ...(utm?.ad_set_name && { adset_name: utm.ad_set_name }),
          ...(utm?.ad_id && { ad_id: utm.ad_id }),
          ...(utm?.ad_name && { ad_name: utm.ad_name }),
        });
      }
    }

    (onNext as (response?: string) => void)(data.planCoverage);
  };

  const onError = (errors: FieldErrors) => {
    trackErrors(errors);
  };

  const handlePrev = () => {
    setPatientInsuranceField("planCoverage", undefined, isSecondaryProvider);
    trackBack();
    onPrev();
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit, onError)}
      className="w-full max-w-[570px] rounded-lg shadow-xl"
    >
      <FormContentContainer stepId={stepId}>
        <HipaaLogo className="size-16 self-center sm:size-20" />
        <h1 className="font-display text-xl sm:text-2xl">
          What type of plan do you have?
        </h1>
        <RadioCardGroup
          options={radioOptions}
          value={selectedValue}
          onChange={handleSelectionChange}
        />
        {errors.planCoverage && (
          <ErrorMessage message={errors.planCoverage.message ?? ""} />
        )}
      </FormContentContainer>
      <ActionButtons onPrev={handlePrev} />
    </form>
  );
};
